import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "../seo/Seo";
import { SITE } from "../seo/states";
import { getPost, getCategory, BLOG_POSTS } from "../seo/blog";
import { PageShell, CTASection } from "../components/PageShell";

export default function BlogArticle() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;
  const cat = getCategory(post.category);
  const url = `${SITE}/blog/${post.slug}`;
  const related = BLOG_POSTS.filter((p) => p.category === post.category && p.slug !== post.slug).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.date,
    author: { "@type": "Organization", name: "Alexa Financial Multiservices" },
    publisher: { "@type": "Organization", name: "Alexa Financial Multiservices", url: SITE },
    mainEntityOfPage: url,
    url,
  };

  return (
    <PageShell
      eyebrow={cat?.name}
      title={post.title}
      subtitle={post.excerpt}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "Blog", to: "/blog" }, { label: cat?.name, to: `/blog/category/${cat?.slug}` }, { label: post.title }]}
    >
      <Seo title={post.metaTitle} description={post.metaDescription} canonical={url} keywords={`${cat?.name}, ${post.title}`} jsonLd={jsonLd} />

      <article className="py-16 md:py-24 bg-[#050505]">
        <div className="max-w-[820px] mx-auto px-5 md:px-10">
          <div className="text-sm text-[#888] mb-10">{post.readingTime} read · {cat?.name}</div>
          {post.sections.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              className="mb-10"
            >
              <h2 className="font-display text-2xl md:text-3xl text-white mb-4">{s.h2}</h2>
              <p className="text-[#b3b3b3] text-base md:text-lg leading-relaxed">{s.body}</p>
            </motion.section>
          ))}

          <div className="glass rounded-2xl p-8 mt-12 text-center">
            <p className="font-display text-xl md:text-2xl text-white">{post.cta}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/#booking" className="rounded-full px-7 py-3.5 bg-[#d4af37] text-[#050505] font-medium hover:bg-[#e9d08a] transition-colors">Book a Consultation</Link>
              <Link to="/services/llc-formation" className="rounded-full px-7 py-3.5 glass text-white hover:bg-white/10 transition-colors">Explore Services</Link>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="font-display text-2xl text-white mb-6">Related articles</h2>
              <div className="space-y-3">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} className="group flex items-center justify-between glass rounded-xl px-6 py-4 hover:border-[#d4af37]/40 transition-colors">
                    <span className="text-[#d4d4d4] group-hover:text-white transition-colors">{r.title}</span>
                    <ArrowUpRight size={16} className="text-[#d4af37]" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </PageShell>
  );
}
